
import React, { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PainelForm } from "@/components/solar/configuracao/PainelForm";
import { InversorForm } from "@/components/solar/configuracao/InversorForm";
import { OutrosItensForm } from "@/components/solar/configuracao/OutrosItensForm";
import { ConfigAutoSection } from "@/components/solar/configuracao/ConfigAutoSection";
import { ConfigManualSection } from "@/components/solar/configuracao/ConfigManualSection";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";
import { EquipamentoSolar } from "@/types";
import { Package, Sliders, Zap } from "lucide-react";

interface EquipamentosTabProps {
  configAutoTab: string;
  setConfigAutoTab: (tab: string) => void;
}

export const EquipamentosTab = ({ configAutoTab, setConfigAutoTab }: EquipamentosTabProps) => {
  // Estados para forms e imagens
  const [paineis, setPaineis] = useState<EquipamentoSolar[]>([]);
  const [inversores, setInversores] = useState<EquipamentoSolar[]>([]);
  const [categoriasOutros, setCategoriasOutros] = useState<{ categoria: string; itens: string[] }[]>([]);
  const [marcasPaineis, setMarcasPaineis] = useState<string[]>([]);
  const [marcasInversores, setMarcasInversores] = useState<string[]>([]);
  const [modelosPorMarca, setModelosPorMarca] = useState<Record<string, string[]>>({});
  
  // Estados para Forms
  const [marcaSelecionada, setMarcaSelecionada] = useState("");
  const [modeloSelecionado, setModeloSelecionado] = useState("");
  const [itemPersonalizado, setItemPersonalizado] = useState(false);
  const [detalhesEquipamento, setDetalhesEquipamento] = useState<any>({});
  const [editandoEquipamento, setEditandoEquipamento] = useState<EquipamentoSolar | null>(null);
  const [categoriaSelecionada, setCategoriaSelecionada] = useState("");
  
  // Estados para upload de imagens
  const [painelImagePreviewTradicional, setPainelImagePreviewTradicional] = useState("");
  const [inversorImagePreviewTradicional, setInversorImagePreviewTradicional] = useState("");
  const [painelImagePreviewMLPE, setPainelImagePreviewMLPE] = useState("");
  const [inversorImagePreviewMLPE, setInversorImagePreviewMLPE] = useState("");
  const [painelImagePreviewHibrido, setPainelImagePreviewHibrido] = useState("");
  const [inversorImagePreviewHibrido, setInversorImagePreviewHibrido] = useState("");
  const [painelImagePreviewOffGrid, setPainelImagePreviewOffGrid] = useState("");
  const [inversorImagePreviewOffGrid, setInversorImagePreviewOffGrid] = useState("");
  
  // Estados para arquivos de imagem
  const [painelImageTradicional, setPainelImageTradicional] = useState<File | null>(null);
  const [inversorImageTradicional, setInversorImageTradicional] = useState<File | null>(null);
  const [painelImageMLPE, setPainelImageMLPE] = useState<File | null>(null);
  const [inversorImageMLPE, setInversorImageMLPE] = useState<File | null>(null);
  const [painelImageHibrido, setPainelImageHibrido] = useState<File | null>(null);
  const [inversorImageHibrido, setInversorImageHibrido] = useState<File | null>(null);
  const [painelImageOffGrid, setPainelImageOffGrid] = useState<File | null>(null);
  const [inversorImageOffGrid, setInversorImageOffGrid] = useState<File | null>(null);
  
  // Carregar dados salvos
  useEffect(() => {
    // Carregar marcas e modelos de painéis
    const marcasPaineisStorage = localStorage.getItem('marcasPaineis');
    const modelosPaineisStorage = localStorage.getItem('modelosPaineis');
    
    if (marcasPaineisStorage) {
      try {
        setMarcasPaineis(JSON.parse(marcasPaineisStorage));
      } catch (error) {
        console.error("Erro ao carregar marcas de painéis:", error);
      }
    }
    
    if (modelosPaineisStorage) {
      try {
        const modelosDePaineis = JSON.parse(modelosPaineisStorage);
        setModelosPorMarca(prev => ({
          ...prev,
          ...modelosDePaineis
        }));
      } catch (error) {
        console.error("Erro ao carregar modelos de painéis:", error);
      }
    }
    
    // Carregar marcas e modelos de inversores
    const marcasInversoresStorage = localStorage.getItem('marcasInversores');
    const modelosInversoresStorage = localStorage.getItem('modelosInversores');
    
    if (marcasInversoresStorage) {
      try {
        setMarcasInversores(JSON.parse(marcasInversoresStorage));
      } catch (error) {
        console.error("Erro ao carregar marcas de inversores:", error);
      }
    }
    
    if (modelosInversoresStorage) {
      try {
        const modelosDeInversores = JSON.parse(modelosInversoresStorage);
        setModelosPorMarca(prev => ({
          ...prev,
          ...modelosDeInversores
        }));
      } catch (error) {
        console.error("Erro ao carregar modelos de inversores:", error);
      }
    }
    
    // Carregar painéis e inversores cadastrados
    const paineisCadastrados = localStorage.getItem('paineisCadastrados');
    const inversoresCadastrados = localStorage.getItem('inversoresCadastrados');
    
    if (paineisCadastrados) {
      try {
        setPaineis(JSON.parse(paineisCadastrados));
      } catch (error) {
        console.error("Erro ao carregar painéis cadastrados:", error);
      }
    }
    
    if (inversoresCadastrados) {
      try {
        setInversores(JSON.parse(inversoresCadastrados));
      } catch (error) {
        console.error("Erro ao carregar inversores cadastrados:", error);
      }
    }
    
    // Carregar categorias de outros itens
    const categoriasOutrosStorage = localStorage.getItem('categoriasOutros');
    
    if (categoriasOutrosStorage) {
      try {
        setCategoriasOutros(JSON.parse(categoriasOutrosStorage));
      } catch (error) {
        console.error("Erro ao carregar categorias de outros itens:", error);
      }
    }
  }, []);
  
  // Manipulador para adicionar novo equipamento
  const handleAdicionarEquipamento = () => {
    toast.success("Equipamento adicionado");
  };
  
  // Manipulador para upload de arquivos
  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>, 
    setFile: (file: File | null) => void, 
    setPreview: (url: string) => void, 
    setFormValue: (value: string) => void
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setFile(file);
      setFormValue(file.name);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div>
        <Tabs defaultValue="paineis" className="w-full">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="paineis" className="text-sm">Painéis</TabsTrigger>
            <TabsTrigger value="inversores" className="text-sm">Inversores</TabsTrigger>
            <TabsTrigger value="outros" className="text-sm">Outros</TabsTrigger>
          </TabsList>
          <TabsContent value="paineis">
            <PainelForm 
              marcasPaineis={marcasPaineis}
              modelosPorMarca={modelosPorMarca}
              marcaSelecionada={marcaSelecionada}
              setMarcaSelecionada={setMarcaSelecionada}
              modeloSelecionado={modeloSelecionado}
              setModeloSelecionado={setModeloSelecionado}
              itemPersonalizado={itemPersonalizado}
              setItemPersonalizado={setItemPersonalizado}
              detalhesEquipamento={detalhesEquipamento}
              setDetalhesEquipamento={setDetalhesEquipamento}
              editandoEquipamento={editandoEquipamento}
              handleAdicionarEquipamento={handleAdicionarEquipamento}
            />
          </TabsContent>
          <TabsContent value="inversores">
            <InversorForm 
              marcasInversores={marcasInversores}
              modelosPorMarca={modelosPorMarca}
              marcaSelecionada={marcaSelecionada}
              setMarcaSelecionada={setMarcaSelecionada}
              modeloSelecionado={modeloSelecionado}
              setModeloSelecionado={setModeloSelecionado}
              itemPersonalizado={itemPersonalizado}
              setItemPersonalizado={setItemPersonalizado}
              detalhesEquipamento={detalhesEquipamento}
              setDetalhesEquipamento={setDetalhesEquipamento}
              editandoEquipamento={editandoEquipamento}
              handleAdicionarEquipamento={handleAdicionarEquipamento}
            />
          </TabsContent>
          <TabsContent value="outros">
            <OutrosItensForm 
              categoriaSelecionada={categoriaSelecionada}
              setCategoriaSelecionada={setCategoriaSelecionada}
              modeloSelecionado={modeloSelecionado}
              setModeloSelecionado={setModeloSelecionado}
              itemPersonalizado={itemPersonalizado}
              setItemPersonalizado={setItemPersonalizado}
              detalhesEquipamento={detalhesEquipamento}
              setDetalhesEquipamento={setDetalhesEquipamento}
              categoriasOutros={categoriasOutros}
              handleAdicionarEquipamento={handleAdicionarEquipamento}
            />
          </TabsContent>
        </Tabs>
      </div>
      
      <div className="lg:col-span-2">
        <Tabs defaultValue="automatic" className="w-full">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="automatic" className="flex items-center gap-2">
              <Sliders className="h-4 w-4" /> Configuração Automática
            </TabsTrigger>
            <TabsTrigger value="manual" className="flex items-center gap-2">
              <Zap className="h-4 w-4" /> Configuração Manual
            </TabsTrigger>
          </TabsList>
          <TabsContent value="automatic">
            <ConfigAutoSection 
              activeTab={configAutoTab}
              setActiveTab={setConfigAutoTab}
              painelImagePreviewTradicional={painelImagePreviewTradicional}
              inversorImagePreviewTradicional={inversorImagePreviewTradicional}
              painelImagePreviewMLPE={painelImagePreviewMLPE}
              inversorImagePreviewMLPE={inversorImagePreviewMLPE}
              painelImagePreviewHibrido={painelImagePreviewHibrido}
              inversorImagePreviewHibrido={inversorImagePreviewHibrido}
              painelImagePreviewOffGrid={painelImagePreviewOffGrid}
              inversorImagePreviewOffGrid={inversorImagePreviewOffGrid}
              setPainelImagePreviewTradicional={setPainelImagePreviewTradicional}
              setInversorImagePreviewTradicional={setInversorImagePreviewTradicional}
              setPainelImagePreviewMLPE={setPainelImagePreviewMLPE}
              setInversorImagePreviewMLPE={setInversorImagePreviewMLPE}
              setPainelImagePreviewHibrido={setPainelImagePreviewHibrido}
              setInversorImagePreviewHibrido={setInversorImagePreviewHibrido}
              setPainelImagePreviewOffGrid={setPainelImagePreviewOffGrid}
              setInversorImagePreviewOffGrid={setInversorImagePreviewOffGrid}
              setPainelImageTradicional={setPainelImageTradicional}
              setInversorImageTradicional={setInversorImageTradicional}
              setPainelImageMLPE={setPainelImageMLPE}
              setInversorImageMLPE={setInversorImageMLPE}
              setPainelImageHibrido={setPainelImageHibrido}
              setInversorImageHibrido={setInversorImageHibrido}
              setPainelImageOffGrid={setPainelImageOffGrid}
              setInversorImageOffGrid={setInversorImageOffGrid}
              handleFileUpload={handleFileUpload}
            />
          </TabsContent>
          <TabsContent value="manual">
            <ConfigManualSection 
              paineis={paineis}
              setPaineis={setPaineis}
              inversores={inversores}
              setInversores={setInversores}
              categoriasOutros={categoriasOutros}
              setCategoriasOutros={setCategoriasOutros}
              marcasPaineis={marcasPaineis}
              marcasInversores={marcasInversores}
              modelosPorMarca={modelosPorMarca}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
